// -----------------------------------------------------------------------
// <copyright file="AppBar.tsx" company="Microsoft">
//     copyright (c) Microsoft. All rights reserved.
// </copyright>
// -----------------------------------------------------------------------

import { IconButton, ITheme, Stack, TooltipHost } from '@fluentui/react';
import { useBoolean, useId } from '@fluentui/react-hooks';

import * as React from 'react';
import { useSelector } from 'react-redux';
import { UserInfo } from '../../../web-legacy/models/apiModels';
import { AutomationIds } from '../../../web-legacy/models/constants/automationIds';
import { IUITabViewInfo } from '../../../web-legacy/models/reduxState';
import {
  currentUITabViewInfoSelector,
  userInfoSelector,
} from '../../../web-legacy/packages/app/selectors/contactUsSelector';
import { appContext } from '../../models/getHelp/appContext';
import {
  appBarToolTipCalloutProps,
  getAppBarButtonStyles,
  getAppBarStyles,
} from '../../self-help/styles/selfHelpStyles';
import { getHelpLogger } from '../../utils/getHelpLogger';
import { getString } from '../../utils/stringLoader';
import { AboutCallout } from './AboutCallout';
import { BackButton } from './BackButton';
import { OcvComponentContainer } from '../../../web-legacy/packages/contactUs/containerComponents/OcvComponentContainer';

export interface AppBarProps {
  theme: ITheme;
}

export const AppBar = (props: AppBarProps): JSX.Element => {
  const userInfo: UserInfo = useSelector(userInfoSelector);
  const currentUITabViewInfo: IUITabViewInfo = useSelector(currentUITabViewInfoSelector);

  const [isAboutCalloutVisible, { toggle: toggleIsAboutCalloutVisible }] = useBoolean(false);
  const aboutButtonId: string = useId('About-button');

  // If no need to render leftArea and rightArea, render nothing
  if (!currentUITabViewInfo?.showAppBarBackButton && !appContext.showAboutAndFeedbackButtons) {
    return null;
  }

  const onAboutButtonClicked = (): void => {
    getHelpLogger.logAboutButtonClick();
    toggleIsAboutCalloutVisible();
  };

  const leftArea: JSX.Element = (
    <Stack horizontal horizontalAlign="start" verticalAlign="center" grow>
      <BackButton theme={props.theme} />
    </Stack>
  );

  // LegalAgeGroup definition at https://review.learn.microsoft.com/en-us/identity/microsoft-identity-platform/age-group?branch=main#values:
  //   Undefined = 0
  //   MinorWithoutParentalConsent = 1
  //   MinorWithParentalConsent = 2
  //   Adult = 3
  //   NotAdult = 4
  //   MinorNoParentalConsentRequired = 5
  // If the value is not null or 0 or 3, user is minor
  const userIsMinor: boolean = !!userInfo?.LegalAgeGroup && userInfo?.LegalAgeGroup !== 3;

  const feedbackButton: JSX.Element = (
    <TooltipHost
      data-automation-id={AutomationIds.FeedbackTooltip}
      content={getString('AppBar_FeedbackButtonToolTip')}
      calloutProps={appBarToolTipCalloutProps}
    >
      <OcvComponentContainer />
    </TooltipHost>
  );

  const rightArea: JSX.Element = (
    <Stack horizontal horizontalAlign="end" verticalAlign="center" grow>
      {userIsMinor ? null : feedbackButton}
      <TooltipHost
        data-automation-id={AutomationIds.AboutTooltip}
        content={getString('AppBar_AboutButtonToolTip')}
        calloutProps={appBarToolTipCalloutProps}
      >
        <IconButton
          iconProps={{ iconName: 'Info' }}
          aria-label={getString('AppBar_AboutButtonToolTip')}
          id={aboutButtonId}
          onClick={onAboutButtonClicked}
          styles={getAppBarButtonStyles(props.theme)}
          aria-haspopup="dialog"
          aria-expanded={isAboutCalloutVisible}
        />
        {isAboutCalloutVisible && <AboutCallout targetId={aboutButtonId} onDismiss={toggleIsAboutCalloutVisible} />}
      </TooltipHost>
    </Stack>
  );

  return (
    <>
      <Stack
        horizontal
        horizontalAlign="space-between"
        styles={getAppBarStyles(props.theme)}
        tabIndex={1}
        aria-label={getString('AppBar_AriaLabel')}
      >
        {currentUITabViewInfo?.showAppBarBackButton && leftArea}
        {appContext.showAboutAndFeedbackButtons && rightArea}
      </Stack>
    </>
  );
};
